import { beginSend, endSend } from 'actions/composition/lib/in_flight_sends';
import clearCommunicationComposition from 'actions/composition/lib/clear_communication_composition';
import CustomerView from 'models/location/customer_view';
import { DomainError } from 'scripts/application/lib/error';
import Err from 'models/err';
import getCompositionsStore from 'actions/customer/lib/get_compositions_store';
import { handleCommonErrors } from 'scripts/application/lib/gateway_error_interactive_handler';
import removeComposition from 'actions/composition/lib/remove_composition';
import SendEmail, {
  asEmailAttachment,
  getDeletedInlineImages,
  getInlineAttachments,
  getRecipientsList,
  validateTotalSize,
  validateUploads,
} from 'actions/composition/send_email';
import SubmitComposition from 'actions/composition/lib/submit_composition';

export default class SendThirdPartyEmail extends SubmitComposition {
  // The first email of a side conversation is not a conversation message: the backend creates a task
  // to hold the thread, so this skips SubmitComposition's conversation-item plumbing.
  run({ compositionId, content }) {
    const composition = this.loadComposition(compositionId);
    if (!composition || !beginSend(composition.id)) {
      return;
    }

    // The in-flight claim is module level, so a throw before the request is out must release it or
    // this draft could never be sent again without a reload.
    try {
      return this.send(composition, content);
    } catch (err) {
      endSend(composition.id);
      throw err;
    }
  }

  send(composition, content) {
    const composed = { ...composition.toJs().content, ...content };
    const recipients = {
      to: getRecipientsList(composed.to),
      cc: getRecipientsList(composed.cc),
      bcc: getRecipientsList(composed.bcc),
    };

    // The backend creates a holding task for the thread, and a task assignee is invalid without a
    // routing group. Send the customer's current conversation assignee routing group (same value the
    // task composer seeds from) so the created task hydrates on the desktop.
    const { routingGroupId } = (this.activeConversation || {}).assignee || {};

    const errors = this.validate(composition, composed, recipients, routingGroupId);
    if (errors.length) {
      this.compositions.setErrors(composition.id, errors);
      this.saveComposedContent(composition, content);
      endSend(composition.id);
      return;
    }

    const email = this.emailPayload(composition, composed, recipients);

    // Nothing is added to history optimistically, so the draft is only cleared once the send lands.
    return this.context.gateways.taskThreadStart
      .add({}, { customerId: this.currentCustomerId, channel: 'EMAIL', routingGroupId, dueAt: composed.dueAt, email })
      .then(() => this.clearComposition(composition))
      .catch(err => this.onSendError(composition, content, err))
      .finally(() => endSend(composition.id));
  }

  validate(composition, composed, recipients, routingGroupId) {
    const emailContent = {
      ...recipients,
      bodyPlain: composed.bodyPlain || '',
      from: composed.from,
      subject: composed.subject || composed.subjectHtml,
    };

    const errors = SendEmail.validateContent(emailContent)
      .concat(validateUploads(composition.attachments))
      .concat(
        validateTotalSize(JSON.stringify({ ...emailContent, bodyHtml: composed.bodyHtml }), composition.attachments)
      );
    if (!routingGroupId) {
      errors.push(
        new Err({
          attr: 'routingGroupId',
          code: Err.Code.BLANK,
          detail: 'This conversation is not assigned to an inbox, so a task cannot be created for it',
        })
      );
    }
    return errors;
  }

  emailPayload(composition, composed, recipients) {
    const bodyHtml = composed.bodyHtml || '';
    getDeletedInlineImages(bodyHtml, composition.attachments).forEach(a => composition.removeAttachment(a.id));

    const uploaded = composition.attachments.map(attachment => asEmailAttachment(composition, attachment));
    const inline = getInlineAttachments(bodyHtml, composed.inlineAttachments);

    return {
      from: composed.from,
      to: asAddresses(recipients.to),
      cc: asAddresses(recipients.cc),
      bcc: asAddresses(recipients.bcc),
      subject: composed.subject || composed.subjectHtml,
      body: { html: composed.bodyHtml, text: composed.bodyPlain },
      attachments: inline.concat(uploaded),
    };
  }

  // Runs after the round trip, so the agent may have moved on: close the composition that was sent,
  // and only touch the current-composition focus when it still points at this one.
  clearComposition(composition) {
    composition.detachUploads();
    getCompositionsStore(this.context, composition.customerId).replace(composition);

    const location = this.context.stores.currentLocation.get();
    if (location instanceof CustomerView && location.currentCompositionId === composition.id) {
      clearCommunicationComposition(this.context);
      return;
    }
    removeComposition(this.context, composition);
  }

  onSendError(composition, content, err) {
    // Keep the draft: nothing was added to history, so a cleared composition would lose the email.
    this.saveComposedContent(composition, content);

    if (err instanceof DomainError) {
      getCompositionsStore(this.context, composition.customerId).setErrors(composition.id, err.errors);
      return;
    }

    handleCommonErrors(this.context, err);
  }
}

function asAddresses(emails) {
  return emails.map(email => ({ email }));
}
